<template>
  <div v-if="isExpanded" class="rounded-2xl border-1 border-solid border-primary px-16 py-16">
    <button
      type="button"
      class="flex items-center gap-16 mb-16 w-full text-left"
      :aria-expanded="true"
      :aria-label="`Close ${title} form`"
      @click="$emit('toggle')"
    >
      <component :is="icon" class="flex-shrink-0" :width="48" :active="isIconActive" :color="iconColor" />
      <p class="text-strong-body">{{ title }}</p>
    </button>
    <div class="flex flex-col gap-16">
      <poppins-dropdown
        :id="`${value}-organization`"
        :model-value="details.organization"
        label-name="Choose an organization"
        :options="organizationOptions"
        :required="true"
        :status="organizationStatus"
        :helper-text="organizationHelperText"
        @update:model-value="updateField('organization', $event)"
      />
      <div class="grid grid-cols-2 gap-16">
        <poppins-dropdown
          :id="`${value}-issue-month`"
          :model-value="details.issueMonth"
          label-name="Issue Month"
          :options="monthOptions"
          @update:model-value="updateField('issueMonth', $event)"
        />
        <poppins-dropdown
          :id="`${value}-issue-year`"
          :model-value="details.issueYear"
          label-name="Issue Year"
          :options="issueYearOptions"
          @update:model-value="updateField('issueYear', $event)"
        />
      </div>
      <div class="grid grid-cols-2 gap-16">
        <poppins-dropdown
          :id="`${value}-expiration-month`"
          :model-value="details.expirationMonth"
          label-name="Expiration Month"
          :options="monthOptions"
          :required="expirationRequired"
          :status="expirationMonthStatus"
          :helper-text="expirationMonthHelperText"
          :disabled="details.doesNotExpire || details.remove"
          @update:model-value="updateField('expirationMonth', $event)"
        />
        <poppins-dropdown
          :id="`${value}-expiration-year`"
          :model-value="details.expirationYear"
          label-name="Expiration Year"
          :options="expirationYearOptions"
          :required="expirationRequired"
          :status="expirationYearStatus"
          :helper-text="expirationYearHelperText"
          :disabled="details.doesNotExpire || details.remove"
          @update:model-value="updateField('expirationYear', $event)"
        />
      </div>
      <poppins-checkbox
        :checkbox-id="`${value}-does-not-expire`"
        :model-value="details.doesNotExpire"
        label-name="This certification does not expire"
        label-class="mb-0"
        :is-disabled="details.remove"
        @update:model-value="updateDoesNotExpire"
      />
      <div v-if="isAdded" class="remove-certification-checkbox pt-16 border-t-1 border-solid border-grey-divider-subdued">
        <poppins-checkbox
          :checkbox-id="`${value}-remove`"
          :model-value="details.remove"
          label-name="Remove Certification"
          label-class="mb-0 font-medium"
          @update:model-value="updateField('remove', $event)"
        />
      </div>
    </div>
  </div>
  <button
    v-else
    type="button"
    class="group flex items-center gap-16 w-full px-16 py-16 rounded-2xl border-1 bg-white text-left transition-colors"
    :class="isAdded ? addedCardClass : emptyCardClass"
    :aria-expanded="false"
    :aria-label="actionLabel"
    @click="$emit('toggle')"
  >
    <component :is="icon" class="flex-shrink-0" :width="48" :active="isIconActive" :color="iconColor" />
    <span class="flex-1 min-w-0">
      <span class="text-strong-body block">{{ title }}</span>
      <span v-if="detailsSummary" class="text-body-3">{{ detailsSummary }}</span>
      <span v-else-if="showNoDetailsAdded" class="text-body-3">No details added</span>
    </span>
    <span
      class="flex-shrink-0 uppercase font-medium text-s tracking-[1px] leading-normal text-primary group-hover:text-brand-contrast"
    >
      {{ actionText }}
    </span>
  </button>
</template>

<script lang="ts">
import PoppinsDropdown from '@/components/dropdown/Dropdown.vue';
import PoppinsCheckbox from '@/components/checkbox/Checkbox.vue';
import helperStatus from '@/helpers/status';
import { hasCertificationDetails, isCertificationExpirationInThePast, type CertificationDetails, type SelectOption } from '../../__types__/CareTypeFormOptions';

export default {
  name: 'CertificationFormCard',
  components: {
    PoppinsDropdown,
    PoppinsCheckbox,
  },
  props: {
    value: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    icon: {
      type: Object,
      required: true,
    },
    isAdded: {
      type: Boolean,
      default: false,
    },
    isExpanded: {
      type: Boolean,
      default: false,
    },
    details: {
      type: Object as () => CertificationDetails,
      required: true,
    },
    organizationOptions: {
      type: Array as () => SelectOption[],
      required: true,
    },
    monthOptions: {
      type: Array as () => SelectOption[],
      required: true,
    },
    issueYearOptions: {
      type: Array as () => SelectOption[],
      required: true,
    },
    expirationYearOptions: {
      type: Array as () => SelectOption[],
      required: true,
    },
    showRequiredError: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['toggle', 'update:details'],
  computed: {
    emptyCardClass: () =>
      'border-dashed border-grey-divider-subdued hover:border-solid hover:border-primary focus-visible:border-solid focus-visible:border-primary',
    addedCardClass: () => 'border-solid border-transparent box-shadow-sm',
    hasDetails(): boolean {
      return hasCertificationDetails(this.details);
    },
    detailsSummary(): string {
      if (!this.hasDetails) return '';

      const parts: string[] = [];
      if (this.details.organization) {
        const organization = this.organizationOptions.find((option) => option.value === this.details.organization);
        parts.push(organization?.display || this.details.organization);
      }

      if (this.details.doesNotExpire) {
        parts.push('Does not expire');
      } else if (this.details.expirationMonth || this.details.expirationYear) {
        const month = this.monthOptions.find((option) => option.value === this.details.expirationMonth);
        const monthLabel = month?.display ? month.display.slice(0, 3) : '';
        parts.push(['Expires', monthLabel, this.details.expirationYear].filter(Boolean).join(' '));
      }

      return parts.join(' · ');
    },
    showNoDetailsAdded(): boolean {
      return this.isAdded && !this.hasDetails;
    },
    isIconActive(): boolean {
      return this.isAdded && this.hasDetails;
    },
    iconColor(): string {
      return this.showNoDetailsAdded ? '#8A8A8F' : '';
    },
    actionText(): string {
      if (!this.isAdded) return 'Add to profile';
      return this.hasDetails ? 'Edit' : 'Add details';
    },
    actionLabel(): string {
      if (!this.isAdded) return `Add ${this.title} to profile`;
      return this.hasDetails ? `Edit ${this.title}` : `Add details for ${this.title}`;
    },
    expirationRequired(): boolean {
      return !this.details.remove && !this.details.doesNotExpire;
    },
    hasPastExpiration(): boolean {
      return this.expirationRequired && isCertificationExpirationInThePast(this.details.expirationMonth, this.details.expirationYear);
    },
    organizationStatus(): number {
      return this.requiredFieldStatus(!this.details.remove && !this.details.organization);
    },
    organizationHelperText(): string {
      return this.requiredHelperText(this.organizationStatus);
    },
    expirationMonthStatus(): number {
      return this.requiredFieldStatus((this.expirationRequired && !this.details.expirationMonth) || this.hasPastExpiration);
    },
    expirationMonthHelperText(): string {
      if (this.showRequiredError && this.hasPastExpiration) return 'Expiration date cannot be in the past';
      return this.requiredHelperText(this.expirationMonthStatus);
    },
    expirationYearStatus(): number {
      return this.requiredFieldStatus((this.expirationRequired && !this.details.expirationYear) || this.hasPastExpiration);
    },
    expirationYearHelperText(): string {
      if (this.showRequiredError && this.hasPastExpiration) return 'Expiration date cannot be in the past';
      return this.requiredHelperText(this.expirationYearStatus);
    },
  },
  methods: {
    requiredFieldStatus(isMissing: boolean): number {
      return this.showRequiredError && isMissing ? helperStatus.Error : helperStatus.Neutral;
    },
    requiredHelperText(status: number): string {
      return status === helperStatus.Error ? 'This field is required' : '';
    },
    updateField(field: keyof CertificationDetails, value: string | boolean) {
      this.$emit('update:details', { ...this.details, [field]: value });
    },
    updateDoesNotExpire(doesNotExpire: boolean) {
      this.$emit('update:details', {
        ...this.details,
        doesNotExpire,
        expirationMonth: doesNotExpire ? '' : this.details.expirationMonth,
        expirationYear: doesNotExpire ? '' : this.details.expirationYear,
      });
    },
  },
};
</script>

<style scoped>
.remove-certification-checkbox :deep(.checkbox + .checkbox-label) {
  color: theme('colors.critical.DEFAULT');
}
</style>
